import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { EditorialMarquee } from "@/components/site/Marquee";
import { Newsletter } from "@/components/site/Newsletter";
import { IMAGES, MANIFESTO, TESTIMONIALS } from "@/lib/content";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.12, 1.24]);

  return (
    <section ref={ref} className="relative min-h-screen pt-24 grain overflow-hidden" data-testid="hero">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-8 items-center min-h-[calc(100vh-6rem)]">
        <div className="lg:col-span-7 pt-10 lg:pt-0">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="overline text-[#B7926A] mb-6"
          >
            Business Relationship &amp; Revenue Mentor · For experienced wedding pros
          </motion.p>

          <h1 className="font-display font-500 text-[#47394E] text-5xl sm:text-7xl lg:text-[6.2rem] leading-[0.92] tracking-tight">
            <MaskedLines lines={["You've built", "the business."]} delay={0.3} />
            <MaskedLines lines={["Let's make revenue", "predictable."]} delay={0.55} className="italic text-[#B7926A]" />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="font-body text-lg text-[#47394E]/75 mt-8 max-w-xl leading-relaxed"
          >
            I'm Jamie — a Business Relationship &amp; Revenue Mentor for experienced wedding
            professionals. I help you uncover hidden revenue, strengthen your referral relationships,
            and build a clear path to consistent bookings — without paid ads or constant hustle.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="flex flex-wrap gap-4 mt-10"
          >
            <Link
              to="/coaching"
              data-testid="hero-coaching-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-[#47394E] text-[#E8DCCF] px-8 py-4 font-medium hover:bg-[#A8B39B] hover:text-[#47394E] transition-colors"
            >
              Explore Coaching
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/community"
              data-testid="hero-networking-cta"
              className="group inline-flex items-center gap-2 rounded-full border border-[#B7926A] text-[#47394E] px-8 py-4 font-medium hover:bg-[#B7926A] hover:text-[#E8DCCF] transition-colors"
            >
              Discover Community
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>

          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            onClick={() => document.getElementById("freebie")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="hero-freebie-link"
            className="mt-6 inline-flex items-center gap-1.5 font-body text-sm text-[#47394E]/70 hover:text-[#B7926A] transition-colors underline underline-offset-4"
          >
            Or grab the free guide
            <ArrowUpRight size={15} />
          </motion.button>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="relative h-[440px] sm:h-[560px] lg:h-[680px] clip-arch overflow-hidden"
          >
            <motion.img
              style={{ y, scale, transformOrigin: "50% 30%", objectPosition: "50% 62%" }}
              src={IMAGES.heroCoaching}
              alt="Jamie M Rapavy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#47394E]/40 to-transparent" />
          </motion.div>
          <div className="absolute -bottom-6 -left-6 bg-white border border-[#EAE3D8] px-6 py-4 hidden sm:block">
            <p className="signature text-3xl text-[#47394E] leading-none">Jamie M Rapavy</p>
            <p className="overline text-[#9C8199] mt-2">Business Relationship &amp; Revenue Mentor</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Manifesto = () => (
  <section className="bg-white py-24 lg:py-36" data-testid="manifesto">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <Reveal>
        <p className="overline text-[#B7926A] mb-4">The philosophy</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#47394E] tracking-tight max-w-3xl leading-none">
          Growth that feels intentional, not exhausting.
        </h2>
      </Reveal>

      <div className="grid md:grid-cols-3 gap-px mt-20 bg-[#EAE3D8]">
        {MANIFESTO.map((m, i) => (
          <Reveal key={m.n} delay={i * 0.1}>
            <div className="bg-[#F7F4EF] p-10 h-full">
              <span className="font-display text-6xl text-[#B7926A]/70">{m.n}</span>
              <h3 className="font-display text-2xl lg:text-3xl text-[#47394E] mt-6">{m.title}</h3>
              <p className="font-body text-[#47394E]/70 mt-4 leading-relaxed">{m.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const DualPaths = () => (
  <section className="py-24 lg:py-36" data-testid="dual-paths">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <Reveal className="mb-16">
        <p className="overline text-[#B7926A] mb-4">Two ways to work together</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#47394E] tracking-tight max-w-2xl leading-none">
          One brand. Two distinct paths.
        </h2>
      </Reveal>

      <div className="grid lg:grid-cols-2 gap-8">
        {[
          {
            to: "/coaching",
            label: "Coaching",
            title: "Private mentorship & systems",
            desc: "1:1 coaching, the Inquiry Follow-Up System, and clarity intensives for steadier revenue.",
            img: IMAGES.journal,
            testid: "path-coaching",
            btn: "bg-[#47394E] text-[#E8DCCF] group-hover:bg-[#A8B39B] group-hover:text-[#47394E]",
            tag: "#A8B39B",
          },
          {
            to: "/community",
            label: "Community",
            title: "A community that refers you",
            desc: "Devoted Wedding Pros: member events, referral relationships, and a place to connect, grow, and belong.",
            img: IMAGES.community,
            testid: "path-networking",
            btn: "bg-[#2C5A63] text-[#E8DCCF] group-hover:bg-[#21474F]",
            tag: "#7BA7AE",
          },
        ].map((p, i) => (
          <Reveal key={p.to} delay={i * 0.12}>
            <Link to={p.to} data-testid={p.testid} className="group block relative overflow-hidden h-[520px]">
              <img
                src={p.img}
                alt={p.label}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#47394E]/90 via-[#47394E]/30 to-transparent" />
              <div className="absolute inset-0 p-10 flex flex-col justify-end text-[#E8DCCF]">
                <span className="overline mb-3" style={{ color: p.tag }}>{p.label}</span>
                <h3 className="font-display text-3xl lg:text-4xl leading-tight">{p.title}</h3>
                <p className="font-body text-[#E8DCCF]/80 mt-3 max-w-md">{p.desc}</p>
                <span className={`mt-6 inline-flex items-center gap-2 w-fit rounded-full px-6 py-3 text-sm font-medium transition-colors ${p.btn}`}>
                  Learn more <ArrowRight size={16} />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section className="bg-[#47394E] text-[#E8DCCF] pt-14 lg:pt-20 pb-24 lg:pb-32 grain relative" data-testid="testimonials">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
      <Reveal>
        <p className="overline text-[#A8B39B] mb-2">In their words</p>
        <span className="font-display text-7xl lg:text-8xl leading-[0.6] text-[#B7926A] block">“</span>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-12 mt-6 items-start">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.12}>
            <blockquote className="font-display text-lg lg:text-xl leading-snug text-[#E8DCCF]/95">
              {t.quote}
            </blockquote>
            <div className="mt-6 pt-4 border-t border-[#E8DCCF]/20 flex items-center gap-4">
              {t.image ? (
                <img
                  src={t.image}
                  alt={t.name}
                  className="w-12 h-12 rounded-full object-cover border border-[#B7926A]/50 shrink-0"
                />
              ) : null}
              <div>
                <p className="font-body font-semibold">{t.name}</p>
                <p className="font-body text-sm text-[#A8B39B]">{t.role}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <div className="text-right">
        <span className="font-display text-7xl lg:text-8xl leading-[0.2] text-[#B7926A] inline-block">”</span>
      </div>
    </div>
  </section>
);

const GuideCTA = () => (
  <section id="freebie" className="py-24 lg:py-36" data-testid="guide-cta">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
      <Reveal className="lg:col-span-6">
        <div className="relative h-[420px] clip-arch overflow-hidden">
          <img src={IMAGES.flowers} alt="Free guide" className="w-full h-full object-cover" />
        </div>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-6">
        <p className="overline text-[#B7926A] mb-4">Free guide</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#47394E] tracking-tight leading-none">
          Where Your Next 5 Bookings Are Hiding
        </h2>
        <ul className="mt-8 space-y-3 font-body text-[#47394E]/75">
          {[
            "A quick clarity exercise to spot missed revenue",
            "A relationship audit that highlights warm contacts",
            "A simple framework to identify five people to reach out to",
            "Clear next steps — so you're never left wondering what to say",
          ].map((li) => (
            <li key={li} className="flex gap-3">
              <span className="text-[#A8B39B] mt-1">✦</span>
              {li}
            </li>
          ))}
        </ul>
        <div className="mt-10">
          <Newsletter />
        </div>
      </Reveal>
    </div>
  </section>
);

const BookCohort = () => (
  <section className="bg-[#47394E] text-[#E8DCCF] py-24 lg:py-32 grain relative" data-testid="book-cohort">
    <div className="max-w-[1100px] mx-auto px-6 lg:px-12 relative z-10">
      <Reveal>
        <p className="overline text-[#A8B39B] mb-5">Founding Cohort | Limited to 10 wedding pros</p>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95]">
          The B.O.O.K. Method
        </h2>
        <p className="font-display text-2xl lg:text-3xl italic text-[#D9C3A8] mt-5 max-w-3xl leading-snug">
          You've built the business. Now let's make your revenue more predictable.
        </p>
        <div className="grid md:grid-cols-2 gap-8 mt-10 font-body text-[#E8DCCF]/85 leading-relaxed">
          <p>
            The B.O.O.K. Method is my 16-week group coaching experience for established wedding
            professionals who want to create more consistent revenue, strengthen their positioning,
            build referral relationships, improve follow-up, and turn more opportunities into bookings.
          </p>
          <p>
            I am opening only 10 spots in this first Founding Cohort so the experience can stay
            personal, hands-on, and implementation focused.
          </p>
        </div>
        <p className="font-display text-2xl text-[#E8DCCF] mt-10">
          Founding Cohort Investment: $297<span className="text-base font-body text-[#E8DCCF]/70">/month for 4 months</span>
        </p>
        <Link
          to="/coaching#apply"
          data-testid="book-cohort-cta"
          className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#E8DCCF] text-[#47394E] px-8 py-4 font-medium hover:bg-[#A8B39B] transition-colors"
        >
          Apply for the Founding Cohort
          <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <EditorialMarquee items={["Clarity", "Connection", "Predictable Revenue", "Warm Follow-Up"]} />
      <Manifesto />
      <BookCohort />
      <DualPaths />
      <Testimonials />
      <GuideCTA />
    </div>
  );
}
